'use client';

import { useState } from 'react';
import { Loader } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Textarea } from '@/shared/components/ui/textarea';
import { ScrollAnimation } from '@/shared/components/ui/scroll-animation';
import { cn } from '@/shared/lib/utils';
import { Section } from '@/shared/types/blocks/landing';

export function FeedbackForm({
  section,
  className,
}: {
  section: Section;
  className?: string;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: '',
    message: '',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCategoryChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      category: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validation
    if (!formData.email) {
      toast.error('Email is required');
      return;
    }

    if (!formData.category) {
      toast.error('Please select a feedback type');
      return;
    }

    if (!formData.message) {
      toast.error('Message is required');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to submit feedback');
      }

      toast.success(
        section.success_message ||
          'Thank you for your feedback! We\'ll review it shortly.'
      );

      // Reset form
      setFormData({
        name: '',
        email: '',
        category: '',
        message: '',
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to submit feedback';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const fields = section.fields || {};
  const categoryOptions =
    fields.category?.options || section.fields?.category?.options || [];

  return (
    <section
      id={section.id}
      className={cn('py-16 md:py-24 bg-muted', section.className, className)}
    >
      <div className="container max-w-2xl">
        <div className="text-center mb-12">
          <ScrollAnimation>
            <h2 className="text-4xl font-semibold text-balance lg:text-5xl">
              {section.title}
            </h2>
          </ScrollAnimation>
          <ScrollAnimation delay={0.15}>
            <p className="mt-4 text-muted-foreground">
              {section.description}
            </p>
          </ScrollAnimation>
        </div>

        <ScrollAnimation delay={0.3}>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name Field */}
            <div>
              <label htmlFor="name" className="text-sm font-medium">
                {fields.name?.label || 'Name'}
              </label>
              <Input
                id="name"
                name="name"
                type="text"
                placeholder={
                  fields.name?.placeholder || 'Your name (optional)'
                }
                value={formData.name}
                onChange={handleChange}
                className="mt-2"
              />
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="text-sm font-medium">
                {fields.email?.label || 'Email'}
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder={
                  fields.email?.placeholder || 'your.email@example.com'
                }
                value={formData.email}
                onChange={handleChange}
                className="mt-2"
                required
              />
            </div>

            {/* Category Field */}
            <div>
              <label htmlFor="category" className="text-sm font-medium">
                {fields.category?.label || 'Feedback Type'}
              </label>
              <Select value={formData.category} onValueChange={handleCategoryChange}>
                <SelectTrigger className="mt-2">
                  <SelectValue
                    placeholder={
                      fields.category?.placeholder || 'Select a type'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {categoryOptions.map((option: any) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Message Field */}
            <div>
              <label htmlFor="message" className="text-sm font-medium">
                {fields.message?.label || 'Message'}
              </label>
              <Textarea
                id="message"
                name="message"
                placeholder={
                  fields.message?.placeholder ||
                  'Tell us what you think...'
                }
                value={formData.message}
                onChange={handleChange}
                className="mt-2 min-h-32"
                required
              />
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full"
              size="lg"
            >
              {isLoading && <Loader className="mr-2 size-4 animate-spin" />}
              {section.button?.label || 'Send Feedback'}
            </Button>
          </form>
        </ScrollAnimation>
      </div>
    </section>
  );
}
